import { LEGAL_DOCUMENTS } from '@dbrain/shared';
import { Button, Icons } from '@dbrain/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useCapabilities } from '../experience.js';

/**
 * Phase 16c: cookies. Oppy uses only the cookies it cannot work without (the session, the CSRF token; Çerez
 * Politikası lists them), so a one-line note says so until it is closed ("Tamam", kept in the browser as
 * `oppy.cerez`). The consent band of B2cTanitim (Seçerek ayarla, Yalnız zorunlu, Tümüne izin ver, equal in weight) is
 * here too, ready for the day a measurement tool comes; it is not shown while there is none.
 */
export const COOKIE_NOTICE_KEY = 'oppy.cerez';
/** There is no measurement tool: the note, not the consent band */
export const MEASUREMENT_COOKIES = false;

function seen(): boolean {
  try {
    return localStorage.getItem(COOKIE_NOTICE_KEY) !== null;
  } catch {
    return false;
  }
}

function remember(choice: string): void {
  try {
    localStorage.setItem(COOKIE_NOTICE_KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
  } catch {
    // storage unavailable (private mode): the note comes back next time
  }
}

export function CookieNotice() {
  const capabilities = useCapabilities();
  const [open, setOpen] = useState(() => !seen());
  // the bireysel edition's public pages; the kurumsal edition runs inside a company
  if (!capabilities.plans || !open) return null;
  const close = (choice: string) => {
    remember(choice);
    setOpen(false);
  };
  return MEASUREMENT_COOKIES ? <CookieConsent onChoice={close} /> : <CookieInfoBar onClose={() => close('necessary')} />;
}

function CookieInfoBar({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="cookie-bar" role="region" aria-label={t('b2cBilling.cookies.label')} data-testid="cookie-bar">
      <p className="cookie-bar__text">
        <Icons.info size={16} aria-hidden="true" />
        <span>
          {t('b2cBilling.cookies.only')}{' '}
          <Link to={`/yasal/${LEGAL_DOCUMENTS.cookies.slug}`} data-testid="cookie-policy">
            {LEGAL_DOCUMENTS.cookies.title}
          </Link>
        </span>
      </p>
      <Button size="sm" onClick={onClose} data-testid="cookie-ok">
        {t('b2cBilling.cookies.ok')}
      </Button>
    </div>
  );
}

/** B2cTanitim's band, for when measurement cookies exist: "Yalnız zorunlu" and "Tümüne izin ver" look the same */
export function CookieConsent({ onChoice }: { onChoice: (choice: 'necessary' | 'all' | 'custom') => void }) {
  const { t } = useTranslation();
  return (
    <div className="cookie-band" role="region" aria-label={t('b2cBilling.cookies.label')} data-testid="cookie-consent">
      <p className="cookie-band__text">
        <Icons.info size={16} aria-hidden="true" />
        <span>
          <strong>{t('b2cBilling.cookies.title')}</strong> {t('b2cBilling.cookies.consent')}{' '}
          <Link to={`/yasal/${LEGAL_DOCUMENTS.cookies.slug}`}>{LEGAL_DOCUMENTS.cookies.title}</Link>
        </span>
      </p>
      <div className="cookie-band__actions">
        <Button variant="secondary" size="sm" onClick={() => onChoice('custom')}>
          {t('b2cBilling.cookies.custom')}
        </Button>
        <Button size="sm" onClick={() => onChoice('necessary')}>
          {t('b2cBilling.cookies.necessary')}
        </Button>
        <Button size="sm" onClick={() => onChoice('all')}>
          {t('b2cBilling.cookies.all')}
        </Button>
      </div>
    </div>
  );
}
